import { sanitizeHtml } from '../../utils/safeHtml';
import React, { useState, useEffect } from 'react';
import { History, Download, Filter, X, ChevronDown, Copy, Check, FileText, ChevronRight } from 'lucide-react';

export default function SystemAuditHistory() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedEntry, setExpandedEntry] = useState(null);
  const [copiedHash, setCopiedHash] = useState(null);
  
  // Filter
  const [filterType, setFilterType] = useState('all');
  const [limit, setLimit] = useState(50);
  
  const entityTypes = [
    { value: 'all', label: 'Alle Bereiche' },
    { value: 'code_deployment', label: '🤖 Moltbot Deployments' },
    { value: 'legal_text', label: 'Legal-Texte' },
    { value: 'pricing_setting', label: 'Preise' },
    { value: 'package', label: 'Pakete' },
    { value: 'astria_tuning', label: 'Astria-Tuning' },
    { value: 'bonus_link', label: 'Referral/Bonus' },
    { value: 'landing_text', label: 'Landing-Page' },
    { value: 'user_account', label: 'User-Accounts' },
    { value: 'user_auth', label: 'Auth' },
    { value: 'user_credit', label: 'Credits/SNAPs' },
    { value: 'payment', label: 'Zahlungen' }
  ];
  
  // 👤 Einfache Erklärungen für Felder
  const fieldLabels = {
    // User/Account
    credits: 'Credits',
    snaps: 'SNAPs',
    free_images: 'Gratis-Bilder',
    free_images_earned: 'Gratis-Bilder verdient',
    free_images_used: 'Gratis-Bilder genutzt',
    account_status: 'Kontostatus',
    email: 'E-Mail',
    name: 'Name',
    display_id: 'User-ID (kurz)',
    role: 'Rolle',
    is_admin: 'Admin-Rechte',
    is_verified: 'Verifiziert',
    last_ip: 'Letzte IP',
    last_user_agent: 'Letztes Gerät',
    last_activity: 'Letzte Aktivität',
    logout_all_before: 'Sessions ungültig seit',
    password_hash: 'Passwort',
    password_changed_at: 'Passwort geändert am',
    
    // Auth/Security
    two_factor_enabled: '2FA aktiviert',
    email_code_enabled: 'E-Mail-Code aktiviert',
    totp_secret: 'TOTP-Secret',
    backup_codes: 'Backup-Codes',
    reset_token: 'Reset-Token',
    reset_token_expires: 'Reset-Token läuft ab',
    google_id: 'Google-ID',
    
    // Payment
    amount: 'Betrag',
    currency: 'Währung',
    status: 'Status',
    
    // Content
    title: 'Titel',
    content: 'Inhalt',
    price: 'Preis',
    price_eur: 'Preis (EUR)',
    enabled: 'Aktiviert',
    
    // Avatar
    auto_extend: 'Auto-Verlängerung',
    expires_at: 'Läuft ab am',
    
    // Bonus
    bonus_credits: 'Bonus-Credits',
    bonus_snaps: 'Bonus-SNAPs',
    
    // Meta (ignorieren in Anzeige)
    created_at: null,
    updated_at: null,
    updated_by: null,
    picture: 'Profilbild'
  };
  
  // Generiert einfache Erklärung für eine Änderung
  const generateSimpleExplanation = (key, oldValue, newValue) => {
    // Felder mit null-Label ignorieren
    if (fieldLabels[key] === null) return null;
    
    const label = fieldLabels[key] || key;
    
    // Spezielle Formatierung für bestimmte Felder
    const formatValue = (val) => {
      if (val === null || val === undefined) return 'nicht gesetzt';
      if (val === true) return 'Ja';
      if (val === false) return 'Nein';
      if (typeof val === 'object') return JSON.stringify(val);
      return String(val);
    };
    
    const oldStr = formatValue(oldValue);
    const newStr = formatValue(newValue);
    
    // Sensible Felder maskieren
    if (['password_hash', 'totp_secret', 'backup_codes', 'reset_token'].includes(key)) {
      return `${label} wurde geändert`;
    }
    
    // Numerische Differenz berechnen
    if (typeof oldValue === 'number' && typeof newValue === 'number') {
      const diff = newValue - oldValue;
      const diffStr = diff > 0 ? `+${diff}` : `${diff}`;
      return `${label}: ${oldStr} → ${newStr} (${diffStr})`;
    }
    
    // Boolean-Änderungen
    if (typeof oldValue === 'boolean' || typeof newValue === 'boolean') {
      if (newValue === true) return `${label} wurde aktiviert`;
      if (newValue === false) return `${label} wurde deaktiviert`;
    }
    
    // Lange Werte kürzen
    const maxLen = 50;
    const oldDisplay = oldStr.length > maxLen ? oldStr.substring(0, maxLen) + '...' : oldStr;
    const newDisplay = newStr.length > maxLen ? newStr.substring(0, maxLen) + '...' : newStr;
    
    // Standard
    return `${label}: "${oldDisplay}" → "${newDisplay}"`;
  };
  
  // 💡 Generiert eine menschliche Zusammenfassung basierend auf den Änderungen
  const generateChangeSummary = (changes, entityType, action) => {
    if (!changes || changes.length === 0) return null;
    
    const changedKeys = changes.map(c => c.key);
    
    // Credits/SNAPs geändert
    if (changedKeys.includes('credits') || changedKeys.includes('snaps') || changedKeys.includes('free_images')) {
      const creditChange = changes.find(c => c.key === 'credits');
      const snapChange = changes.find(c => c.key === 'snaps');
      const freeChange = changes.find(c => c.key === 'free_images');
      
      let parts = [];
      if (creditChange) {
        const diff = (creditChange.newValue || 0) - (creditChange.oldValue || 0);
        parts.push(diff > 0 ? `${diff} Credits gutgeschrieben` : `${Math.abs(diff)} Credits abgezogen`);
      }
      if (snapChange) {
        const diff = (snapChange.newValue || 0) - (snapChange.oldValue || 0);
        parts.push(diff > 0 ? `${diff} SNAPs gutgeschrieben` : `${Math.abs(diff)} SNAPs abgezogen`);
      }
      if (freeChange) {
        const diff = (freeChange.newValue || 0) - (freeChange.oldValue || 0);
        parts.push(diff > 0 ? `${diff} Gratis-Bilder hinzugefügt` : `${Math.abs(diff)} Gratis-Bilder verbraucht`);
      }
      return `💰 ${parts.join(', ')}`;
    }
    
    // Passwort geändert
    if (changedKeys.includes('password_hash')) {
      return '🔐 Passwort wurde geändert';
    }
    
    // 2FA Änderungen
    if (changedKeys.includes('two_factor_enabled') || changedKeys.includes('email_code_enabled')) {
      const twoFA = changes.find(c => c.key === 'two_factor_enabled');
      if (twoFA) {
        return twoFA.newValue ? '🔒 2FA wurde aktiviert' : '🔓 2FA wurde deaktiviert';
      }
    }
    
    // Login/Aktivität
    if (changedKeys.includes('last_activity') || changedKeys.includes('last_ip')) {
      return '👤 User-Aktivität (Login/Session)';
    }
    
    // Sessions invalidiert
    if (changedKeys.includes('logout_all_before')) {
      return '🚪 Alle Sessions wurden beendet';
    }
    
    // Account Status
    if (changedKeys.includes('account_status')) {
      const statusChange = changes.find(c => c.key === 'account_status');
      return `⚡ Account-Status: ${statusChange.oldValue} → ${statusChange.newValue}`;
    }
    
    // Preise/Pakete
    if (entityType === 'pricing_setting' || entityType === 'package') {
      return '💵 Preise oder Paket-Einstellungen geändert';
    }
    
    // Legal
    if (entityType === 'legal_text') {
      return '⚖️ Rechtstexte wurden aktualisiert';
    }
    
    // Zahlung
    if (entityType === 'payment') {
      return '💳 Zahlungsdaten aktualisiert';
    }
    
    // Fallback basierend auf Anzahl Änderungen
    if (changes.length === 1) {
      return `✏️ ${fieldLabels[changedKeys[0]] || changedKeys[0]} wurde geändert`;
    }
    
    return `✏️ ${changes.length} Felder wurden geändert`;
  };
  
  useEffect(() => {
    loadHistory();
  }, [filterType, limit]);
  
  const loadHistory = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('snapshot_token');
      
      const params = new URLSearchParams();
      if (filterType !== 'all') {
        params.append('entity_type', filterType);
      }
      params.append('limit', limit.toString());
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/system-audit/history?${params}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (!response.ok) throw new Error('Fehler beim Laden');
      
      const data = await response.json();
      setEntries(data.entries || []);
      setLoading(false);
    } catch (err) {
      console.error('Error loading history:', err);
      setError('Fehler beim Laden der System-Historie');
      setLoading(false);
    }
  };
  
  const copyToClipboard = (text, entryId) => {
    navigator.clipboard.writeText(text);
    setCopiedHash(entryId);
    setTimeout(() => setCopiedHash(null), 2000);
  };
  
  const downloadAuditProof = (entry, format = 'json') => {
    try {
      let content, filename, mimeType;
      
      if (format === 'json') {
        // JSON-Format: Vollständig maschinenlesbar
        const proof = {
          audit_id: entry.audit_id,
          entity: `${entry.entity_type}/${entry.entity_key}`,
          version: entry.version_number,
          action: entry.action,
          timestamp_utc: entry.timestamp,
          changed_by: entry.changed_by,
          source: entry.source,
          sha256_hash: entry.content_hash,
          opentimestamps: {
            provider: entry.timestamp_provider,
            status: entry.timestamp_status,
            proof: entry.timestamp_proof ? 'included' : null
          },
          snapshot: entry.snapshot,
          description: entry.action_description
        };
        
        content = JSON.stringify(proof, null, 2);
        filename = `audit_${entry.entity_key}_v${entry.version_number}_${Date.now()}.json`;
        mimeType = 'application/json';
      } else {
        // TXT-Format: Menschenlesbar
        content = `SNAPSHOT PRO - AUDIT-BEWEIS
═══════════════════════════════════════

📋 AUDIT-INFORMATION
Audit-ID: ${entry.audit_id}
Entity: ${entry.entity_type}/${entry.entity_key}
Version: v${entry.version_number}
Aktion: ${entry.action}

⏰ ZEITSTEMPEL
UTC-Zeit: ${entry.timestamp}
Changed by: ${entry.changed_by}
Source: ${entry.source}

🔒 INTEGRITÄT
SHA-256 Hash (vollständig):
${entry.content_hash}

OpenTimestamps:
- Provider: ${entry.timestamp_provider}
- Status: ${entry.timestamp_status}
- Proof: ${entry.timestamp_proof ? 'Vorhanden (.ots separat downloadbar)' : 'Nicht verfügbar'}

📝 BESCHREIBUNG
${entry.action_description || 'Keine Beschreibung'}

📊 SNAPSHOT (Zustand nach Änderung)
${JSON.stringify(entry.snapshot, null, 2)}

═══════════════════════════════════════
Generiert: ${new Date().toISOString()}
`;
        filename = `audit_${entry.entity_key}_v${entry.version_number}_${Date.now()}.txt`;
        mimeType = 'text/plain';
      }
      
      const blob = new Blob([content], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
    }
  };
  
  const downloadOtsProof = (entry) => {
    if (!entry.timestamp_proof) return;
    
    try {
      const hexProof = entry.timestamp_proof;
      const bytes = new Uint8Array(hexProof.match(/.{1,2}/g).map(byte => parseInt(byte, 16)));
      const blob = new Blob([bytes], { type: 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${entry.entity_key}_v${entry.version_number}.ots`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('OTS Download error:', err);
    }
  };
  
  const renderHighlightedJson = (snapshot, changedKeys, isAfter = false) => {
    if (!snapshot || typeof snapshot !== 'object') return 'null';
    
    const lines = [];
    const entries = Object.entries(snapshot);
    
    lines.push('{');
    entries.forEach(([key, value], index) => {
      const isLast = index === entries.length - 1;
      const isChanged = changedKeys.includes(key);
      const valueStr = typeof value === 'object' ? JSON.stringify(value, null, 2).replace(/\n/g, '\n  ') : JSON.stringify(value);
      
      const lineClass = isChanged 
        ? (isAfter ? 'text-[#10B981] font-semibold' : 'text-red-400 font-semibold')
        : 'text-white/70';
      
      const bgClass = isChanged
        ? (isAfter ? 'bg-[#10B981]/10' : 'bg-red-500/10')
        : '';
      
      lines.push(
        `  <span class="${bgClass} ${lineClass} ${isChanged ? 'px-1 -mx-1 rounded' : ''}"><span class="text-blue-400">"${key}"</span>: ${valueStr}${isLast ? '' : ','}</span>`
      );
    });
    lines.push('}');
    
    return lines.join('\n');
  };
  
  const renderDiff = (currentSnapshot, previousSnapshot, action, versionNumber, entityType) => {
    if (!currentSnapshot || typeof currentSnapshot !== 'object') return null;
    
    // Falls KEINE vorherige Version: Zeige CREATE/INITIAL
    if (!previousSnapshot || typeof previousSnapshot !== 'object') {
      const isCreate = action === 'create' || versionNumber === 1;
      
      return (
        <div className="mt-3 p-3 bg-[#10B981]/10 rounded border border-[#10B981]/30">
          <p className="text-[#10B981] text-xs font-semibold mb-2">
            ✨ {isCreate ? 'NEU ERSTELLT' : 'INITIAL-ZUSTAND'} (Version {versionNumber}):
          </p>
          <div className="space-y-1">
            {Object.entries(currentSnapshot).map(([key, value]) => {
              if (key === '_id' || key === 'updated_at' || key === 'created_at' || key === 'updated_by') return null;
              
              return (
                <div key={key} className="flex items-start gap-2 text-xs">
                  <span className="text-white/60 min-w-[140px] font-medium">{key}:</span>
                  <span className="text-[#10B981] font-mono break-all">
                    {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                  </span>
                </div>
              );
            })}
          </div>
          <p className="text-white/40 text-xs mt-3 italic">
            ℹ️ Keine Vorher/Nachher-Ansicht möglich - dies ist die erste Version
          </p>
        </div>
      );
    }
    
    // VORHER/NACHHER-Vergleich
    const changes = [];
    const changedKeys = [];
    const allKeys = new Set([...Object.keys(currentSnapshot), ...Object.keys(previousSnapshot)]);
    
    allKeys.forEach(key => {
      if (key === '_id' || key === 'updated_at' || key === 'created_at' || key === 'updated_by') return;
      
      const oldValue = previousSnapshot[key];
      const newValue = currentSnapshot[key];
      
      // Nur Änderungen anzeigen
      if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
        changes.push({ key, oldValue, newValue });
        changedKeys.push(key);
      }
    });
    
    if (changes.length === 0) {
      return (
        <div className="mt-3 p-3 bg-black/40 rounded border border-white/10">
          <p className="text-white/60 text-xs">Keine Feldänderungen (Metadaten-Update)</p>
        </div>
      );
    }
    
    return (
      <div className="mt-3 space-y-3">
        {/* 👤 EINFACHE ERKLÄRUNG - Oben, gut sichtbar */}
        <div className="p-3 bg-blue-500/10 rounded-lg border border-blue-500/30">
          {/* 💡 Zusammenfassung für Praktikanten */}
          <div className="mb-3 pb-2 border-b border-blue-500/20">
            <p className="text-blue-300 text-sm font-medium">
              {generateChangeSummary(changes, entityType, action)}
            </p>
          </div>
          
          <p className="text-blue-400/70 text-xs font-semibold mb-2">Details:</p>
          <div className="space-y-1">
            {changes.map(({ key, oldValue, newValue }) => {
              const explanation = generateSimpleExplanation(key, oldValue, newValue);
              if (!explanation) return null;
              return (
                <div key={key} className="flex items-center gap-2 text-xs">
                  <span className="text-white/70">{explanation}</span>
                </div>
              );
            })}
          </div>
        </div>
        
        {/* 📝 TECHNISCHE DETAILS - Darunter, einklappbar */}
        <details className="group">
          <summary className="cursor-pointer text-xs text-white/50 hover:text-white/70 transition-all p-2 bg-black/20 rounded border border-white/10 flex items-center gap-2">
            <ChevronRight className="w-3 h-3 transition-transform group-open:rotate-90" />
            📝 Technische Details anzeigen ({changes.length} Felder)
          </summary>
          <div className="mt-2 p-3 bg-black/40 rounded border border-white/10">
            <p className="text-white/60 text-xs font-semibold mb-2">🔄 ÄNDERUNGEN ({changes.length}):</p>
            <div className="space-y-2">
              {changes.map(({ key, oldValue, newValue }) => (
                <div key={key} className="text-xs border-l-2 border-white/20 pl-3">
                  <p className="text-white/70 font-semibold mb-1">{key}:</p>
                  <div className="space-y-0.5">
                    <div className="flex items-start gap-2">
                      <span className="text-red-400/80 shrink-0">❌ Vorher:</span>
                      <code className="text-red-400 font-mono text-xs break-all">
                        {typeof oldValue === 'object' ? JSON.stringify(oldValue) : String(oldValue)}
                      </code>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-[#10B981]/80 shrink-0">✅ Nachher:</span>
                      <code className="text-[#10B981] font-mono text-xs break-all">
                        {typeof newValue === 'object' ? JSON.stringify(newValue) : String(newValue)}
                      </code>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </details>
        
        {/* VOLLSTÄNDIGE SNAPSHOTS MIT HIGHLIGHTING */}
        <details className="group">
          <summary className="cursor-pointer text-xs text-white/60 hover:text-white/80 transition-all p-2 bg-black/20 rounded border border-white/10">
            🔍 Vollständige JSON-Snapshots anzeigen (geänderte Felder markiert)
          </summary>
          <div className="mt-2 grid grid-cols-2 gap-3">
            {/* VORHER */}
            <div className="p-3 bg-red-500/5 rounded border border-red-500/20">
              <p className="text-red-400 text-xs font-semibold mb-2">📋 VORHER (v{versionNumber - 1}):</p>
              <pre 
                className="text-xs font-mono overflow-auto max-h-80 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(renderHighlightedJson(previousSnapshot, changedKeys, false)) }}
              />
            </div>
            
            {/* NACHHER */}
            <div className="p-3 bg-[#10B981]/5 rounded border border-[#10B981]/20">
              <p className="text-[#10B981] text-xs font-semibold mb-2">📋 NACHHER (v{versionNumber}):</p>
              <pre 
                className="text-xs font-mono overflow-auto max-h-80 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(renderHighlightedJson(currentSnapshot, changedKeys, true)) }}
              />
            </div>
          </div>
        </details>
      </div>
    );
  };
  
  const getEntityTypeLabel = (type) => {
    const labels = {
      'code_deployment': '🤖 Moltbot',
      'system_update': '🔧 System',
      'legal_text': '⚖️ Legal',
      'pricing_setting': '💰 Preise',
      'package': '📦 Pakete',
      'astria_tuning': '🎨 Astria',
      'bonus_link': '🎁 Bonus',
      'landing_text': '🏠 Landing',
      'user_account': '👤 Account',
      'user_auth': '🔐 Auth',
      'user_credit': '💳 Credits',
      'payment': '💰 Zahlung',
      'system_setting': '⚙️ System'
    };
    return labels[type] || type;
  };
  
  const getSourceBadge = (source) => {
    if (source === 'automated_deployment') {
      return <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded">🤖 MOLTBOT</span>;
    }
    if (source === 'backend_system' || source === 'migration_script') {
      return <span className="px-2 py-0.5 bg-orange-500/20 text-orange-500 text-xs rounded">SYSTEM</span>;
    }
    if (source === 'admin_ui') {
      return <span className="px-2 py-0.5 bg-[#10B981]/20 text-[#10B981] text-xs rounded">ADMIN</span>;
    }
    if (source === 'api_endpoint') {
      return <span className="px-2 py-0.5 bg-blue-500/20 text-blue-500 text-xs rounded">API</span>;
    }
    return <span className="px-2 py-0.5 bg-white/10 text-white/60 text-xs rounded">{source}</span>;
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-black">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#10B981]/30 border-t-[#10B981]"></div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-black grain-texture text-white p-6">
      <div className="max-w-7xl mx-auto">
        
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <History className="w-8 h-8 text-[#10B981]" />
            <h1 className="text-3xl font-bold">System-Historie</h1>
          </div>
          <p className="text-white/60">
            Alle Änderungen systemweit - lückenlos, chronologisch, kryptografisch beweisbar
          </p>
        </div>
        
        {/* Filter */}
        <div className="mb-6 flex gap-4 items-center">
          <div className="relative flex-1 max-w-xs">
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white appearance-none"
            >
              {entityTypes.map(type => (
                <option key={type.value} value={type.value} className="bg-black">
                  {type.label}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 pointer-events-none" />
          </div>
          
          <div className="text-sm text-white/60">
            {entries.length} Einträge
          </div>
        </div>
        
        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
            <p className="text-red-500">{error}</p>
          </div>
        )}
        
        {/* Timeline */}
        <div className="space-y-3">
          {entries.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-white/40">Keine Einträge gefunden</p>
            </div>
          ) : (
            entries.map((entry, index) => (
              <div 
                key={entry.audit_id || index}
                className="p-4 bg-white/5 border border-white/10 rounded-lg hover:border-[#10B981]/30 transition-all"
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-[#10B981]/20 flex items-center justify-center text-[#10B981] font-bold text-sm">
                      v{entry.version_number}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-white font-medium">{getEntityTypeLabel(entry.entity_type)}</span>
                        <span className="text-white/60">→</span>
                        <span className="text-white/80">{entry.entity_key}</span>
                        {getSourceBadge(entry.source)}
                      </div>
                      <p className="text-white/50 text-xs">
                        {entry.timestamp ? new Date(entry.timestamp).toLocaleString('de-DE', { timeZone: 'UTC', hour12: false }) + ' UTC' : 'N/A'}
                      </p>
                      {/* 💡 PRAKTIKANTEN-INFO - Große blaue Box */}
                      <div className="mt-2 p-2 bg-blue-500/10 rounded-lg border border-blue-500/20">
                        {entry.action_description ? (
                          <p className="text-blue-400 text-sm font-medium">📋 {entry.action_description}</p>
                        ) : (() => {
                          // Mit previous_snapshot: Diff berechnen
                          if (entry.previous_snapshot && entry.snapshot) {
                            const changes = [];
                            const allKeys = new Set([...Object.keys(entry.snapshot || {}), ...Object.keys(entry.previous_snapshot || {})]);
                            allKeys.forEach(key => {
                              if (['_id', 'updated_at', 'created_at', 'updated_by'].includes(key)) return;
                              const oldVal = entry.previous_snapshot[key];
                              const newVal = entry.snapshot[key];
                              if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
                                changes.push({ key, oldValue: oldVal, newValue: newVal });
                              }
                            });
                            if (changes.length > 0) {
                              const summary = generateChangeSummary(changes, entry.entity_type, entry.action);
                              return <p className="text-blue-400 text-sm font-medium">{summary}</p>;
                            }
                          }
                          
                          // FALLBACK
                          const fallbackSummary = {
                            'code_deployment': '🤖 Moltbot hat Code deployed',
                            'system_update': '🔧 System-Update durchgeführt',
                            'user_auth': '👤 User-Session oder Login-Daten aktualisiert',
                            'user_account': '👤 Account-Daten geändert',
                            'user_credit': '💰 Credits/SNAPs angepasst',
                            'payment': '💳 Zahlungsinformation aktualisiert',
                            'legal_text': '⚖️ Rechtliche Texte geändert',
                            'pricing_setting': '💵 Preiseinstellung geändert',
                            'package': '📦 Paket-Konfiguration geändert',
                            'bonus_link': '🎁 Bonus/Referral aktualisiert',
                            'landing_text': '🏠 Landing-Page Text geändert',
                            'astria_tuning': '🎨 KI-Einstellungen angepasst',
                            'system_setting': '⚙️ System-Einstellung geändert'
                          };
                          const fallback = fallbackSummary[entry.entity_type] || (entry.entity_type ? `✏️ ${entry.entity_type} wurde aktualisiert` : '🔧 System-Änderung');
                          return <p className="text-blue-400/70 text-sm">{fallback}</p>;
                        })()}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-white/40 uppercase">{entry.action}</span>
                    <button
                      onClick={() => setExpandedEntry(expandedEntry === entry.audit_id ? null : entry.audit_id)}
                      className="p-1 hover:bg-white/10 rounded transition-all"
                    >
                      <ChevronRight className={`w-4 h-4 text-white/60 transition-transform ${expandedEntry === entry.audit_id ? 'rotate-90' : ''}`} />
                    </button>
                  </div>
                </div>
                
                {/* Basis-Info */}
                <div className="grid grid-cols-3 gap-3 text-xs mb-3">
                  <div>
                    <p className="text-white/40 mb-1">Geändert von</p>
                    <p className="text-white/80 font-mono truncate">
                      {entry.changed_by 
                        ? (typeof entry.changed_by === 'object' ? entry.changed_by?.email || JSON.stringify(entry.changed_by) : entry.changed_by)
                        : <span className="text-purple-400">🤖 Moltbot (System)</span>
                      }
                    </p>
                  </div>
                  
                  <div>
                    <p className="text-white/40 mb-1">Blockchain-Zeitstempel</p>
                    <p className={`font-medium ${
                      entry.timestamp_status === 'pending' ? 'text-yellow-500' :
                      entry.timestamp_status === 'confirmed' ? 'text-[#10B981]' :
                      'text-red-500'
                    }`}>
                      {entry.timestamp_status === 'pending' ? '⏳ Pending (24-48h)' :
                       entry.timestamp_status === 'confirmed' ? '✓ Bitcoin-bestätigt' :
                       '❌ Failed'}
                    </p>
                    <p className="text-white/30 text-xs mt-0.5">Kein Handeln nötig</p>
                  </div>
                  
                  <div>
                    <p className="text-white/40 mb-1">SHA-256 Hash</p>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => copyToClipboard(entry.content_hash, entry.audit_id)}
                        className="flex items-center gap-1 text-[#10B981] font-mono text-xs hover:text-[#059669] transition-all group"
                        title="Hash kopieren"
                      >
                        <span className="truncate max-w-[100px]">{entry.content_hash?.substring(0, 12)}...</span>
                        {copiedHash === entry.audit_id ? (
                          <Check className="w-3 h-3" />
                        ) : (
                          <Copy className="w-3 h-3 opacity-0 group-hover:opacity-100" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Erweiterte Details (expandable) */}
                {expandedEntry === entry.audit_id && (
                  <div className="mt-3 pt-3 border-t border-white/10 space-y-3">
                    
                    {/* Vollständiger Hash */}
                    <div>
                      <p className="text-white/60 text-xs font-semibold mb-1">🔒 SHA-256 HASH (vollständig):</p>
                      <div className="p-2 bg-black/40 rounded border border-white/10 flex items-center justify-between gap-2">
                        <code className="text-[#10B981] text-xs font-mono break-all">
                          {entry.content_hash}
                        </code>
                        <button
                          onClick={() => copyToClipboard(entry.content_hash, `full_${entry.audit_id}`)}
                          className="p-1 hover:bg-white/10 rounded transition-all shrink-0"
                          title="Vollständigen Hash kopieren"
                        >
                          {copiedHash === `full_${entry.audit_id}` ? (
                            <Check className="w-4 h-4 text-[#10B981]" />
                          ) : (
                            <Copy className="w-4 h-4 text-white/60" />
                          )}
                        </button>
                      </div>
                    </div>
                    
                    {/* Diff-Ansicht */}
                    {renderDiff(entry.snapshot, entry.previous_snapshot, entry.action, entry.version_number, entry.entity_type)}
                    
                  </div>
                )}
                
                {/* Downloads */}
                <div className="flex items-center justify-end gap-2 pt-3 border-t border-white/10 mt-3">
                  <button
                    onClick={() => downloadAuditProof(entry, 'txt')}
                    className="px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white text-xs rounded transition-all flex items-center gap-1.5"
                    data-testid={`download-txt-${entry.audit_id}`}
                  >
                    <FileText className="w-3.5 h-3.5" />
                    .txt Beweis
                  </button>
                  
                  <button
                    onClick={() => downloadAuditProof(entry, 'json')}
                    className="px-3 py-1.5 bg-[#10B981] text-black text-xs rounded hover:bg-[#059669] transition-all flex items-center gap-1.5 font-medium"
                    data-testid={`download-json-${entry.audit_id}`}
                  >
                    <Download className="w-3.5 h-3.5" />
                    .json Beweis
                  </button>
                  
                  {entry.timestamp_proof && (
                    <button
                      onClick={() => downloadOtsProof(entry)}
                      className="px-3 py-1.5 bg-orange-500/20 hover:bg-orange-500/30 text-orange-500 text-xs rounded transition-all flex items-center gap-1.5"
                      data-testid={`download-ots-${entry.audit_id}`}
                      title="Optional: Blockchain-Beweis für technische Verifikation"
                    >
                      <Download className="w-3.5 h-3.5" />
                      .ots (optional)
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
        
        {/* Info */}
        <div className="mt-8 p-4 bg-[#10B981]/5 border border-[#10B981]/20 rounded-lg">
          <p className="text-white/80 text-sm font-semibold mb-2">🔒 Zentrale Audit-Historie</p>
          <ul className="text-white/60 text-sm space-y-1">
            <li>✓ Append-only (Einträge können nicht gelöscht oder geändert werden)</li>
            <li>✓ Jeder Eintrag mit SHA-256 Hash für Integrität</li>
            <li>✓ OpenTimestamps = Blockchain-Beweis (automatisch, kein Handeln nötig)</li>
            <li>✓ Download als .json oder .txt für mobile Geräte</li>
          </ul>
        </div>
        
      </div>
    </div>
  );
}
// 🤖 Auto-Deploy Test 13:44
